import {
  Modal as ChakraModal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  Skeleton,
  Table,
  Tr,
  Th,
  Td,
  Link,
  List,
  ListItem,
  Tooltip,
  Heading
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import PreviewModal from '../../../components/previewModal'
import { EntregaDeTrabalhoService } from '../../../services/EntregaDeTrabalho/EntregaDeTrabalhoService'
import { courseFormat, downloadLink, previewLink, ucFirst } from './helper'
import { EEntregaDeTrabalhoType, IEntregaDeTrabalhoComplete, IEntregaDeTrabalhoUpload } from './models'

interface IProps {
  isOpen: boolean
  onClose: () => void
  id?: number
}

export function EntregaDeTrabalhoViewModal({ isOpen, onClose, id }: IProps) {
  const service = new EntregaDeTrabalhoService()
  const [loading, setLoading] = useState(true)
  const [workSubmission, setWorkSubmission] = useState<IEntregaDeTrabalhoComplete>({} as any)
  const [previewModal, setPreviewModal] = useState(false)
  const [previewData, setPreviewData] = useState<{ url: string; fileName: string; fileSize: number }>({
    url: '',
    fileName: '',
    fileSize: 0
  })

  useEffect(() => {
    if (!id || !isOpen) return

    setLoading(true)
    service.get(id).then((response) => {
      setWorkSubmission(response)
      setLoading(false)
    })
  }, [isOpen])

  const handleOpenPreview = (upload: IEntregaDeTrabalhoUpload) => {
    setPreviewData({
      fileName: upload.original_name,
      url: previewLink(upload),
      fileSize: Number(upload.size)
    })

    setPreviewModal(true)
  }

  return (
    <>
      <ChakraModal isOpen={isOpen} onClose={onClose} size="3xl" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Skeleton isLoaded={!loading} width="50%">
              {EEntregaDeTrabalhoType[workSubmission.type] || 'Entrega de trabalho'}
            </Skeleton>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Table>
              <Tr>
                <Th>Status:</Th>
                <Td>
                  <Skeleton isLoaded={!loading}>{ucFirst(workSubmission.status) || '-'}</Skeleton>
                </Td>
              </Tr>
              <Tr>
                <Th>Nome do Aluno:</Th>
                <Td>
                  <Skeleton isLoaded={!loading}>{workSubmission.name || '-'}</Skeleton>
                </Td>
              </Tr>
              <Tr>
                <Th>CPF do Aluno:</Th>
                <Td>
                  <Skeleton isLoaded={!loading}>{workSubmission.cpf || '-'}</Skeleton>
                </Td>
              </Tr>
              <Tr>
                <Th>Nome do Responsável:</Th>
                <Td>
                  <Skeleton isLoaded={!loading}>{workSubmission.responsable_name || '-'}</Skeleton>
                </Td>
              </Tr>
              <Tr>
                <Th>CPF do Responsável:</Th>
                <Td>
                  <Skeleton isLoaded={!loading}>{workSubmission.responsable_cpf || '-'}</Skeleton>
                </Td>
              </Tr>
              <Tr>
                <Th>E-mail:</Th>
                <Td>
                  <Skeleton isLoaded={!loading}>{workSubmission.email || '-'}</Skeleton>
                </Td>
              </Tr>
              <Tr>
                <Th>Telefone:</Th>
                <Td>
                  <Skeleton isLoaded={!loading}>{workSubmission.phone || '-'}</Skeleton>
                </Td>
              </Tr>
              <Tr>
                <Th>Curso:</Th>
                <Td>
                  <Skeleton isLoaded={!loading}>{workSubmission.course || '-'}</Skeleton>
                </Td>
              </Tr>
              <Tr>
                <Th>Modalidade:</Th>
                <Td>
                  <Skeleton isLoaded={!loading}>
                    {courseFormat[workSubmission.format as 'presencial' | 'online' | 'online-ao-vivo'] || '-'}
                  </Skeleton>
                </Td>
              </Tr>
              <Tr>
                <Th>Professor:</Th>
                <Td>
                  <Skeleton isLoaded={!loading}>{workSubmission.teacher || '-'}</Skeleton>
                </Td>
              </Tr>
              <Tr>
                <Th>Dias de aula:</Th>
                <Td>
                  <Skeleton isLoaded={!loading}>{workSubmission['class-days'] || '-'}</Skeleton>
                </Td>
              </Tr>
              <Tr>
                <Th>Horário das aulas</Th>
                <Td>
                  <Skeleton isLoaded={!loading}>
                    {workSubmission['class-hour-start']?.substring(0, 5)} às{' '}
                    {workSubmission['class-hour-ends']?.substring(0, 5)}
                  </Skeleton>
                </Td>
              </Tr>
              <Tr>
                <Th>Data de submissão:</Th>
                <Td>
                  <Skeleton isLoaded={!loading}>
                    {new Date(workSubmission.created_at).toLocaleString('pt-BR').replace(',', ' -').slice(0, -3)}
                  </Skeleton>
                </Td>
              </Tr>
              <Tr>
                <Th>Arquivos:</Th>
                <Td>
                  {loading ? (
                    <Skeleton>Loading...</Skeleton>
                  ) : (
                    <List>
                      {workSubmission.uploads.map((upload) => (
                        <ListItem key={upload.id}>
                          <div className="row mb-2">
                            <div className="col-10">
                              {upload.original_name}{' '}
                              <small className="text-muted">
                                ({(Number(upload.size) / 1024 / 1024).toFixed(2)} MB)
                              </small>
                            </div>
                            <div className="col-1">
                              <Tooltip label="Abrir preview">
                                <Link color={'blue.500'} onClick={() => handleOpenPreview(upload)}>
                                  <i className="lni lni-eye"></i>
                                </Link>
                              </Tooltip>
                            </div>
                            <div className="col-1">
                              <Tooltip label="Fazer download">
                                <Link target="_blank" href={downloadLink(upload)} rel="noreferrer" color={'blue.500'}>
                                  <i className="lni lni-download"></i>
                                </Link>
                              </Tooltip>
                            </div>
                          </div>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Td>
              </Tr>
            </Table>
            {workSubmission.review && (
              <>
                <Heading size="md" mt={4} opacity={0.8} ml={6}>
                  Avaliação
                </Heading>
                <Table>
                  <Tr>
                    <Th>Professor:</Th>
                    <Td colSpan={3}>{workSubmission.review.teacher}</Td>
                  </Tr>
                  <Tr>
                    <Th w="15%">Data de Início:</Th>
                    <Td w="35%">
                      {new Date(workSubmission.review.start_date + 'T00:00:00').toLocaleDateString('pt-BR')}
                    </Td>
                    <Th w="15%">Data de Término:</Th>
                    <Td w="35%">
                      {new Date(workSubmission.review.end_date + 'T00:00:00').toLocaleDateString('pt-BR')}
                    </Td>
                  </Tr>
                  <Tr>
                    <Th>Carga Horária:</Th>
                    <Td>{workSubmission.review.course_load}</Td>
                    <Th>Nº do Contrato:</Th>
                    <Td>{workSubmission.review.contract_number}</Td>
                  </Tr>
                  <Tr>
                    <Th>Cód. Turma:</Th>
                    <Td>{workSubmission.review.class_code}</Td>
                    <Th>Nota:</Th>
                    <Td>{Number(workSubmission.review.grade).toFixed(2)}</Td>
                  </Tr>
                  <Tr>
                    <Th>Comentário:</Th>
                    <Td colSpan={3}>{workSubmission.review.observations}</Td>
                  </Tr>
                </Table>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onClose} type="button">
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </ChakraModal>

      <PreviewModal
        isOpen={previewModal}
        onClose={() => {
          setPreviewModal(false)
        }}
        url={previewData.url}
        fileName={previewData.fileName}
        fileSize={previewData.fileSize}
      />
    </>
  )
}
